import {
  Badge,
  Icon,
  Input,
  Radiobutton,
  Switch,
  TextField,
} from "@elaniintc/goes-design-system";
import type React from "react";
import type { RenderFieldContext } from "./types";
import { addToArray, removeFromArray } from "./utils";

export function renderTextField({
  fieldConfig,
  value,
  updateFormValue,
}: RenderFieldContext) {
  return (
    <TextField
      key={fieldConfig.key}
      label={fieldConfig.label}
      placeholder={fieldConfig.placeholder}
      required={fieldConfig.required}
      value={
        typeof value === "string" || typeof value === "number" ? value : ""
      }
      onChange={(e) => updateFormValue(fieldConfig.key, e.target.value)}
      error={
        fieldConfig.validation?.custom
          ? fieldConfig.validation.custom(value) || undefined
          : undefined
      }
      helperText={fieldConfig.helperText}
      disabled={fieldConfig.disabled}
    />
  );
}

export function renderSwitchField({
  fieldConfig,
  value,
  updateFormValue,
}: RenderFieldContext) {
  const trueText = fieldConfig.customProps?.switchTrueText || "Si";
  const falseText = fieldConfig.customProps?.switchFalseText || "No";
  return (
    <div key={fieldConfig.key} className="flex flex-col gap-1">
      <label
        htmlFor={fieldConfig.key}
        className="font-semibold text-body-loud leading-2xl"
      >
        {fieldConfig.label}
        {fieldConfig.required && <span className="text-danger">*</span>}
      </label>
      <div className="flex items-center gap-3 mt-1">
        <Switch
          checked={typeof value === "boolean" ? value : false}
          onCheckedChange={(checked) =>
            updateFormValue(fieldConfig.key, checked)
          }
          disabled={fieldConfig.disabled}
        />
        <span className="text-body font-normal">
          {value ? trueText : falseText}
        </span>
      </div>
      {fieldConfig.helperText && (
        <p className="text-xs text-body-quiet mt-1">{fieldConfig.helperText}</p>
      )}
    </div>
  );
}

export function renderNumberField({
  fieldConfig,
  value,
  updateFormValue,
}: RenderFieldContext) {
  const labelSuffix = fieldConfig.customProps?.labelSuffix;
  return (
    <div key={fieldConfig.key} className="flex flex-col gap-1">
      {labelSuffix ? (
        <div className="flex items-center justify-between">
          <label
            htmlFor={fieldConfig.key}
            className="font-semibold text-body-loud text-sm leading-2xl"
          >
            {fieldConfig.label}
            {fieldConfig.required && (
              <span className="text-sm text-danger ml-1">*</span>
            )}
          </label>
          <span className="text-xs text-body-quiet">{labelSuffix}</span>
        </div>
      ) : (
        <label
          htmlFor={fieldConfig.key}
          className="font-semibold text-body-loud text-sm leading-2xl"
        >
          {fieldConfig.label}
          {fieldConfig.required && (
            <span className="text-sm text-danger ml-1">*</span>
          )}
        </label>
      )}
      <TextField
        label=""
        type="number"
        placeholder={fieldConfig.placeholder}
        className="w-full"
        value={value?.toString() || ""}
        onChange={(e) =>
          updateFormValue(
            fieldConfig.key,
            e.target.value ? Number(e.target.value) : "",
          )
        }
        disabled={fieldConfig.disabled}
      />
      {fieldConfig.helperText && (
        <p className="text-xs text-body-quiet mt-1">{fieldConfig.helperText}</p>
      )}
    </div>
  );
}

export function renderMultiselectField({
  fieldConfig,
  value,
  updateFormValue,
}: RenderFieldContext) {
  const selectedValues = Array.isArray(value) ? value : [];
  return (
    <div key={fieldConfig.key} className="flex flex-col gap-1">
      <label
        htmlFor={fieldConfig.key}
        className="font-semibold text-body-loud leading-2xl"
      >
        {fieldConfig.label}
        {fieldConfig.required && <span className="text-danger">*</span>}
      </label>
      <div className="space-y-2 mt-1">
        {fieldConfig.options?.map((option) => (
          <div key={option.value} className="flex items-center gap-3">
            <input
              type="checkbox"
              id={`${fieldConfig.key}-${option.value}`}
              checked={selectedValues.includes(option.value)}
              onChange={(e) => {
                const newValues = e.target.checked
                  ? addToArray(selectedValues, option.value)
                  : removeFromArray(selectedValues, option.value);
                updateFormValue(fieldConfig.key, newValues);
              }}
              className="w-4 h-4 rounded border-neutral-quiet"
            />
            <label
              htmlFor={`${fieldConfig.key}-${option.value}`}
              className="text-body text-sm cursor-pointer"
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>
      {fieldConfig.helperText && (
        <p className="text-xs text-body-quiet mt-1">{fieldConfig.helperText}</p>
      )}
    </div>
  );
}

export function renderRadioField({
  fieldConfig,
  value,
  updateFormValue,
}: RenderFieldContext) {
  return (
    <div key={fieldConfig.key} className="flex flex-col gap-1">
      <label
        htmlFor={fieldConfig.key}
        className="font-semibold text-body-loud leading-2xl"
      >
        {fieldConfig.label}
        {fieldConfig.required && <span className="text-danger">*</span>}
      </label>
      <div className="space-y-2 mt-1">
        {fieldConfig.options?.map((option) => (
          <div key={option.value} className="flex items-center gap-3">
            <Radiobutton
              id={`${fieldConfig.key}-${option.value}`}
              name={fieldConfig.key}
              value={option.value}
              checked={value === option.value}
              onChange={() => updateFormValue(fieldConfig.key, option.value)}
            />
            <label
              htmlFor={`${fieldConfig.key}-${option.value}`}
              className="text-body text-sm cursor-pointer"
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>
      {fieldConfig.helperText && (
        <p className="text-xs text-body-quiet mt-1">{fieldConfig.helperText}</p>
      )}
    </div>
  );
}

export function renderDefaultValueField({
  fieldConfig,
  value,
  updateFormValue,
}: RenderFieldContext) {
  const helperTextIcon =
    fieldConfig.customProps?.helperTextIcon || "circle-info";
  const helperTextColor =
    fieldConfig.customProps?.helperTextColor ?? "text-body-quiet";
  return (
    <div key={fieldConfig.key} className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label
          htmlFor={fieldConfig.key}
          className="font-semibold text-body-loud leading-2xl"
        >
          {fieldConfig.label}
        </label>
        {!fieldConfig.required && (
          <span className="text-xs text-body-quiet">Opcional</span>
        )}
      </div>
      <div className="flex items-center w-full relative">
        <Input
          className="w-full border-neutral-quiet"
          placeholder={fieldConfig.placeholder}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => updateFormValue(fieldConfig.key, e.target.value)}
          disabled={fieldConfig.disabled}
        />
      </div>
      {fieldConfig.helperText && (
        <div className="flex items-center gap-1.5 mt-1">
          <Icon
            name={helperTextIcon}
            type="far"
            className={`${helperTextColor} text-xs`}
          />
          <p className={`text-xs ${helperTextColor}`}>
            {fieldConfig.helperText}
          </p>
        </div>
      )}
    </div>
  );
}

export function renderCustomField({ fieldConfig }: RenderFieldContext) {
  if (fieldConfig.customProps?.type !== "info_tags") return null;
  const { title, tags } = fieldConfig.customProps;
  return (
    <div key={fieldConfig.key} className="flex flex-col gap-2">
      {title && <p className="text-sm text-body font-normal">{title}</p>}
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map(
            (tag: {
              type: string;
              label: string;
              icon?: string | React.ReactNode;
            }) => (
              <Badge
                key={tag.label}
                variant="neutral"
                size="sm"
                className="text-body-quiet bg-neutral-object border border-neutral-quiet"
              >
                {tag.icon && <span className="mr-1">{tag.icon}</span>}
                {tag.label}
              </Badge>
            ),
          )}
        </div>
      )}
    </div>
  );
}
